import type { Ring, Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { horizontalCap, orientedFace } from '../fabric/solids';
import type { WaterStepsSpec } from './waterSteps';
import { stepsProfile, treadY } from './waterSteps';

/** Continuous masonry tiers wrap two adjacent faces of a landing. Their shared outer
 * corner is part of each L tread, rather than two rectangular flights leaving a hole. */
export function buildCornerWaterSteps(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: WaterStepsSpec,
  returnDepth: number
): void {
  stepsProfile(spec); // Validate the same riser/foot constraints as a straight flight.
  if (!(returnDepth > 0)) throw new RangeError('Corner water steps need a positive return depth');
  const half = spec.width / 2,
    run = spec.going * spec.treads;
  const at = (u: number, v: number, y: number): Vec3 => [u, y, -v];
  const wall = (a: Vec2, b: Vec2, y0: number, y1: number, outward: Vec3) => {
    orientedFace(
      builder,
      style,
      frame,
      [at(a[0], a[1], y0), at(b[0], b[1], y0), at(b[0], b[1], y1), at(a[0], a[1], y1)],
      outward
    );
  };
  const foot: Ring = [
    [-returnDepth, -half - run],
    [run, -half - run],
    [run, half],
    [0, half],
    [0, -half],
    [-returnDepth, -half],
  ];
  horizontalCap(builder, style, frame, foot, spec.footY, false);
  for (let k = 1; k <= spec.treads; k++) {
    const a = (k - 1) * spec.going,
      b = k * spec.going,
      y = treadY(spec, k);
    const ring: Ring = [
      [-returnDepth, -half - b],
      [b, -half - b],
      [b, half],
      [a, half],
      [a, -half - a],
      [-returnDepth, -half - a],
    ];
    horizontalCap(builder, style, frame, ring, y, true);
    const below = k < spec.treads ? treadY(spec, k + 1) : spec.footY;
    wall([b, -half - b], [b, half], below, y, [1, 0, 0]);
    wall([-returnDepth, -half - b], [b, -half - b], below, y, [0, 0, 1]);
    // Closed backing ends, each following its tier instead of covering the lower tread.
    wall([a, half], [b, half], spec.footY, y, [0, 0, -1]);
    wall([-returnDepth, -half - b], [-returnDepth, -half - a], spec.footY, y, [-1, 0, 0]);
  }
  wall([0, -half], [0, half], spec.footY, treadY(spec, 1), [-1, 0, 0]);
  wall([-returnDepth, -half], [0, -half], spec.footY, treadY(spec, 1), [0, 0, -1]);
}
